import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeTwoToneIcon from '@mui/icons-material/AccessTimeTwoTone';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import commitedIcon from '../../../../assets/images/commited.svg';
import velocityIcon from '../../../../assets/images/velocity_sprint.svg';
import rolloverIcon from '../../../../assets/images/rollover.svg';
import lockIcon from '../../../../assets/images/lock_icon.svg';
import emptyStateIcon from '../../../../assets/images/empty_state_icon.svg';
import { COLORS } from '../../../../shared/colors';


const SprintSummaryGrids = ({ sprintId, apiData }) => {
  const { t } = useTranslation();

  const spSummary = apiData?.data?.sp_summary || {};
  const ticketHealth = apiData?.data?.ticket_health || {};

  const isSpSummaryEmpty = Object.keys(spSummary).length === 0 || Object.values(spSummary).every(val => val === null || val === undefined);
  const isTicketHealthEmpty = Object.keys(ticketHealth).length === 0 || Object.values(ticketHealth).every(val => val === null || val === undefined);

  if (isSpSummaryEmpty && isTicketHealthEmpty) {
    return (
      <div className="sprint-summary-grids">
        <div className="sm-card sm-empty-state-card summary-empty">
          <div className="sm-empty-state-icon-wrapper summary-icon">
            <img src={emptyStateIcon} alt="Empty State" />
          </div>
          <h3 className="sm-empty-state-title summary-title">{t('sprintManagement.noSummaryDataTitle')}</h3>
          <p className="sm-empty-state-desc summary-desc">
            {t('sprintManagement.noSummaryDataDesc')}
          </p>
        </div>
      </div>
    );
  }

  const metric = (value) => value == null ? 0 : value;
  const metricVelocity = (value) => value == null ? 'N/A' : value;

  return (
    <div className="sprint-summary-grids">
      {/* Sprint Summary Box */}
      <div className="summary-section">
        <Accordion defaultExpanded className="sm-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="sm-accordion-summary">
            <h2 className="card-title">{t('sprintManagement.sprintSummaryTitle')}</h2>
          </AccordionSummary>
          <AccordionDetails className="sm-accordion-details">
            <div className="summary-grid">
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.committed')}</div>
                  <img src={commitedIcon} alt="Committed" className="metric-icon img-icon" />
                </div>
                <div className="metric-value color-committed">
                  {metric(spSummary.total_sp)} <span className="unit">SP</span>
                </div>
                <div className="metric-subtitle color-committed">{t('sprintManagement.subtitleBaseline')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.completed')}</div>
                  <CheckIcon className="metric-icon brand-tint" />
                </div>
                <div className="metric-value color-completed">
                  {metric(spSummary.completed_sp)} <span className="unit">SP</span>
                </div>
                <div className="metric-subtitle color-completed">{t('sprintManagement.subtitleOnTrack')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.incomplete')}</div>
                  <AccessTimeTwoToneIcon className="metric-icon brand-tint" />
                </div>
                <div className="metric-value color-incomplete">
                  {metric(spSummary.incomplete_sp)} <span className="unit">SP</span>
                </div>
                <div className="metric-subtitle color-incomplete">{t('sprintManagement.subtitleNotStarted')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.velocity')}</div>
                  <img src={velocityIcon} alt="Velocity" className="metric-icon img-icon" />
                </div>
                <div className="metric-value color-velocity">
                  {metricVelocity(spSummary.velocity)} {spSummary.velocity != null && <span className="unit">%</span>}
                </div>
                <div className="metric-subtitle color-velocity">
                  {spSummary.average_velocity_change ? `${spSummary.average_velocity_change} ${t('sprintManagement.average')}` : ''}
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>

      {/* Ticket Health Box */}
      <div className="summary-section">
        <Accordion defaultExpanded className="sm-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="sm-accordion-summary">
            <h2 className="card-title">{t('sprintManagement.ticketHealthTitle')}</h2>
          </AccordionSummary>
          <AccordionDetails className="sm-accordion-details">
            <div className="summary-grid">
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.blocked')}</div>
                  <img src={lockIcon} alt="Blocked" className="metric-icon img-icon" />
                </div>
                <div className="metric-value color-blocked">
                  {metric(ticketHealth.blocked_count)}
                </div>
                <div className="metric-subtitle color-blocked">{t('sprintManagement.subtitleNeedsAttention')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.rolloverRisk')}</div>
                  <img src={rolloverIcon} alt="Rollover Risk" className="metric-icon img-icon" />
                </div>
                <div className="metric-value color-rollover">
                  {metric(ticketHealth.roll_over_risk)}
                </div>
                <div className="metric-subtitle color-rollover">{t('sprintManagement.subtitleOffTrack')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.inReview')}</div>
                  <RateReviewOutlinedIcon className="metric-icon" style={{ color: COLORS.brand.primaryDarkAlpha }} />
                </div>
                <div className="metric-value color-review">
                  {metric(ticketHealth.pending_review)}
                </div>
                <div className="metric-subtitle color-review">{t('sprintManagement.subtitleAwaitingApproval')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <div className="metric-title">{t('sprintManagement.notStarted')}</div>
                  <PriorityHighOutlinedIcon className="metric-icon" style={{ color: COLORS.brand.primaryDarkAlpha }} />
                </div>
                <div className="metric-value color-notstarted">
                  {metric(ticketHealth.pending_high_priority)}
                </div>
                <div className="metric-subtitle color-notstarted">{t('sprintManagement.subtitleHighPriority')}</div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export default SprintSummaryGrids;
